import { useContext, useEffect, useMemo } from 'react';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { useQuery } from '@apollo/client';

import { DataContext, DataContextActionType } from '../../../../../context/DataContext';
import {
  SELLER_DEALS_WITH_CARVES,
  SellerDealsWithCarves_user_company_deals,
} from '../../../../../query/SellerDealsWithCarves';
import { useActiveTradesContext } from '../ActiveTradesContext';
import { ComparisonData } from '../comparisonTable.config';

interface CarveAnalyticsCfg {
  label: string;
  value: string | number;
  isBidPX: boolean;
}

export const useBidComparison = () => {
  const navigate = useNavigate();

  const { dispatch } = useContext(DataContext);

  const { clearSelectedListingIds } = useActiveTradesContext();

  const [searchParams] = useSearchParams();
  const idsParam = searchParams.get('ids');
  const selectedIds = idsParam ? idsParam.split(',') : [];

  let maxBidPX = 0;
  let minBidPX = 0;
  let averageBidPX = 0;

  const handleClearSelectedListingIds = () => {
    clearSelectedListingIds();
    navigate(`/marketplace/active_trades/bids`);
  };

  const { data, loading, error } = useQuery(SELLER_DEALS_WITH_CARVES, {
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    dispatch({
      type: DataContextActionType.SET_LOADING,
      payload: loading,
    });
  }, [loading, dispatch]);

  const deals: SellerDealsWithCarves_user_company_deals[] = data?.user?.company?.deals;

  const comparisonData: ComparisonData[] = useMemo(
    () =>
      !!deals?.length
        ? (selectedIds
            .map((compositeId) => {
              const [dealId, bidId] = compositeId.split(':');

              const deal = deals.find((d) => d.id === dealId);
              if (!deal) return {};

              const bid = deal.bids.find((b) => b.bid_id === bidId);
              if (!bid) return {};

              return { ...bid, deal } as ComparisonData;
            })
            .filter(Boolean) as ComparisonData[])
        : [],
    [deals],
  );

  if (!!comparisonData?.length) {
    const bidValues = comparisonData
      ?.map((b) => b?.bid_basis_points)
      .filter((v): v is number => typeof v === 'number');

    if (!!bidValues.length) {
      maxBidPX = Math.max(...bidValues);
      minBidPX = Math.min(...bidValues);
      averageBidPX = bidValues.reduce((sum, val) => sum + val, 0) / bidValues.length;
    }
  }

  const carveAnalyticsCfg: CarveAnalyticsCfg[][] = useMemo(
    () => [
      [
        //todo clear the values to paste
        // {
        //   label: 'Institutions Bidding',
        //   value: '???',
        //   isBidPX: false,
        // },
        // {
        //   label: 'Carves per Institution',
        //   value: '???',
        //   isBidPX: false,
        // },
        {
          label: 'Average Bid Price',
          value: averageBidPX,
          isBidPX: true,
        },
      ],
      [
        {
          label: 'Highest Bid Px ',
          value: maxBidPX,
          isBidPX: true,
        },
        {
          label: 'Lowest Bid Px',
          value: minBidPX,
          isBidPX: true,
        },
      ],
    ],
    [comparisonData, averageBidPX, maxBidPX, minBidPX],
  );

  return {
    loading,
    selectedIds,
    handleClearSelectedListingIds,
    comparisonData,
    carveAnalyticsCfg,
    bidPX: {
      min: minBidPX,
      max: maxBidPX,
      avg: averageBidPX,
    },
  };
};
